//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from './service';
import BroodLineModel from '../models/brood-line.model';

/**
 * Service for managing brood lines data (Master Data).
 *
 * Provides methods to fetch and manage brood lines-related data.
 * Inherits CRUD operations and HTTP utilities from the generic {@link Service} class.
 *
 * @module BroodLinesService
 */
@Injectable({
  providedIn: 'root'
})
export class BroodLinesService extends Service<BroodLineModel> {

  /**
   * Stores ongoing HTTP request subscriptions.
   * Used to cancel active requests and prevent memory leaks.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of BroodLinesService.
   *
   * @param {HttpClient} httpClient - The HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/broodlines/');
  }

  /**
   * Fetches the complete list of brood lines (Master Data).
   *
   * @method
   * @returns {Promise<Array<BroodLineModel>>} A Promise resolving with an array of BroodLineModel objects.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * This method is typically called in {@link BroodLinesComponent} to retrieve the full brood lines list.
   */
  public getAllBroodLinesList(): Promise<Array<BroodLineModel>> {
    return new Promise<Array<BroodLineModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<BroodLineModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests by unsubscribing from their observables.
   * Ensures proper cleanup and prevents memory leaks.
   *
   * Typically called in components when they are destroyed to cancel pending requests.
   *
   * @method
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
